import { useState } from 'react';
import { MAX_NOTE_FILES } from './note-files';
import { saveNote, type MissedStep } from './note-save';
import { useCatalogList } from './NoteAllocationFields';
import {
  allocationChanged,
  allocationOf,
  allocationSavable,
  type Allocation,
} from './project-shares';
import type { Report, ReportInputWave2 } from './types';
import { useNoteFilesList, usePendingFiles } from './use-note-files';

export type ParitySave =
  | { readonly ok: false; readonly status: number; readonly code: string }
  | { readonly ok: true; readonly report: Report; readonly missed: readonly MissedStep[] };

/**
 * #915 W1: the note form's projects, client and files, and the one «Salva» that sends them with
 * the note's own fields. `target` is the note being edited, or the one a partial save created.
 */
export function useNoteParityForm(open: boolean, target: Report | undefined) {
  const [allocation, setAllocation] = useState<Allocation>(() =>
    allocationOf(target?.projects, target?.client),
  );
  const [baseline, setBaseline] = useState<Allocation>(() =>
    allocationOf(target?.projects, target?.client),
  );
  const [removals, setRemovals] = useState<string[]>([]);
  const [missed, setMissed] = useState<readonly MissedStep[]>([]);
  const projects = useCatalogList('projects', open);
  const clients = useCatalogList('clients', open);
  const saved = useNoteFilesList(target?.id, target?.version ?? '');
  const savedFiles = target ? saved.list : undefined;
  const held =
    savedFiles?.status === 'ready'
      ? savedFiles.items.filter((file) => !removals.includes(file.documentId)).length
      : 0;
  const files = usePendingFiles(MAX_NOTE_FILES - held);

  const toggleRemoval = (documentId: string): void =>
    setRemovals((current) =>
      current.includes(documentId)
        ? current.filter((id) => id !== documentId)
        : [...current, documentId],
    );

  const save = async (input: ReportInputWave2): Promise<ParitySave> => {
    setMissed([]);
    const out = await saveNote({
      report: target,
      input,
      allocation,
      allocationChanged: allocationChanged(baseline, allocation),
      uploads: files.pending,
      removals,
    });
    if (!out.ok) return out;
    const saved = out.report;
    // What the server now holds is the new baseline; what it refused stays in the form, so the
    // next «Salva» sends it again.
    setBaseline(allocationOf(saved.projects, saved.client));
    files.keep(out.missed.flatMap((step) => (step.kind === 'upload' ? [step.key] : [])));
    setRemovals(out.missed.flatMap((step) => (step.kind === 'remove' ? [step.documentId] : [])));
    setMissed(out.missed);
    return out;
  };

  return {
    allocation,
    setAllocation,
    savable: allocationSavable(allocation) && !files.preparing,
    projects,
    clients,
    savedFiles,
    removals,
    toggleRemoval,
    files,
    missed,
    save,
  } as const;
}
